import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  /** A formatted figure such as "12", "1,204" or "KES 1.25M"; the first number in it is animated. */
  value: string;
  /** Milliseconds for one count. */
  duration?: number;
}

const NUMBER = /\d[\d,]*(?:\.\d+)?/;

function parse(value: string) {
  const match = value.match(NUMBER);
  if (!match || match.index === undefined) return null;
  const raw = match[0];
  return {
    prefix: value.slice(0, match.index),
    suffix: value.slice(match.index + raw.length),
    target: Number(raw.replace(/,/g, '')),
    decimals: raw.includes('.') ? raw.split('.')[1].length : 0,
    grouped: raw.includes(','),
  };
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Counts a stat up from 0 on first show, and from the old figure to the new
 * one when it changes. Shows the final value at once when the viewer has
 * reduced motion on.
 */
export default function CountUp({ value, duration = 800 }: CountUpProps) {
  const parsed = parse(value);
  const target = parsed?.target ?? 0;
  const from = useRef(0);
  const [current, setCurrent] = useState(() => (prefersReducedMotion() ? target : 0));

  useEffect(() => {
    if (!parsed || prefersReducedMotion()) {
      setCurrent(target);
      from.current = target;
      return;
    }
    const start = from.current;
    let frame = 0;
    let startedAt: number | null = null;
    const step = (now: number) => {
      startedAt ??= now;
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(start + (target - start) * eased);
      if (progress < 1) frame = requestAnimationFrame(step);
      else from.current = target;
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      from.current = target;
    };
    // `parsed` is derived from `value`; re-run only when the figure changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  if (!parsed) return <>{value}</>;

  const shown = parsed.grouped
    ? current.toLocaleString('en-US', {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
      })
    : current.toFixed(parsed.decimals);

  return (
    <span className="tabular-nums" aria-label={value}>
      <span aria-hidden="true">
        {parsed.prefix}
        {shown}
        {parsed.suffix}
      </span>
    </span>
  );
}
